import type { CheckResourcesResponse, CheckResourcesResult, ResourceCheck } from '@cerbos/core';

import { ANY_ID } from './types';
import type { CerbosActionType } from './types';

/**
 * Describes a Cerbos resource kind together with the permission-name → action
 * mapping the application cares about. When an action is provided as an array
 * the resulting permission is only `true` when every action is allowed.
 */
export interface ResourceConfig<TKind extends string = string, TAction extends string = string> {
  kind: TKind;
  actionMap: Record<string, CerbosActionType<TAction> | CerbosActionType<TAction>[]>;
  scope?: string;
  attributes?: Record<string, string | number | boolean>;
}

interface CreateResourceCheckOptions<
  TKind extends string = string,
  TAction extends string = string,
> extends ResourceConfig<TKind, TAction> {
  /** Resource instance id; defaults to the wildcard {@link ANY_ID}. */
  id?: string;
  /** Cerbos resource scope; takes precedence over the per-config `scope`. */
  resourceScope?: string;
}

interface CreateResourceChecksOptions<
  TKind extends string = string,
  TAction extends string = string,
> {
  configs: ResourceConfig<TKind, TAction>[];
  id?: string;
  resourceScope?: string;
}

/**
 * Build a single Cerbos {@link ResourceCheck} for **one specific resource instance**.
 *
 * Use this when checking permissions for a **known entity ID** — typically called
 * inside a `.map()` over a list of entities so each entity gets its own check.
 *
 * @example
 * ```ts
 * // Per-item pattern: each role gets its own permission entry
 * const resources = roles.map((role) =>
 *   createResourceCheck({ kind: RoleCommands.Role, id: String(role.id), actionMap: { canDelete: 'delete' } })
 * );
 * ```
 *
 * @see {@link createResourceChecks} for checking multiple resource kinds under a single shared ID
 */
export function createResourceCheck<
  TKind extends string = string,
  TAction extends string = string,
>({
  kind,
  actionMap,
  attributes,
  scope,
  id,
  resourceScope,
}: CreateResourceCheckOptions<TKind, TAction>): ResourceCheck {
  const actions = (Object.values(actionMap) ?? []).flat();

  const attr: Record<string, string | number | boolean> = {};

  if (attributes) {
    Object.assign(attr, attributes);
  }

  return {
    resource: {
      kind,
      id: id ?? ANY_ID,
      scope: resourceScope ?? scope,
      attr,
    },
    actions,
  };
}

/**
 * Build a batch of Cerbos {@link ResourceCheck}s for **multiple resource kinds** that
 * all share the same resource ID (typically {@link ANY_ID}).
 *
 * Use this for **scalar / principal-level permission checks** where you want to know
 * "can this user do X?" across several resource kinds at once — not per-entity.
 * All configs receive the same `id` and `resourceScope`.
 *
 * @example
 * ```ts
 * // Scalar pattern: flat permission set, all checked under ANY_ID
 * const resources = createResourceChecks({
 *   configs: [
 *     { kind: LoanCommands.Loan, actionMap: { canViewLoans: 'read' } },
 *     { kind: LoanCommands.Loan, actionMap: { canCreateLoan: 'create' } },
 *   ],
 *   id: ANY_ID,
 * });
 * ```
 *
 * @see {@link createResourceCheck} for checking a single resource instance by its own ID
 */
export function createResourceChecks<
  TKind extends string = string,
  TAction extends string = string,
>({ configs, id, resourceScope }: CreateResourceChecksOptions<TKind, TAction>): ResourceCheck[] {
  return configs.map(({ actionMap, scope, kind }) =>
    createResourceCheck<TKind, TAction>({
      kind,
      actionMap,
      scope,
      id,
      resourceScope,
    }),
  );
}

interface GetPermissionsOptions<TKind extends string = string, TAction extends string = string> {
  permissionConfigs: ResourceConfig<TKind, TAction>[];
  data?: CheckResourcesResponse | CheckResourcesResult;
  id: string;
  scope?: string;
}

/**
 * Extract permission booleans from a Cerbos response for the provided configs.
 *
 * Generic `T` lets callers provide a specific permission shape (e.g. `{ canRead: boolean }`)
 * and receive that exact type back for type safety.
 */
export function getPermissions<
  T extends Record<string, boolean>,
  TKind extends string = string,
  TAction extends string = string,
>(options: GetPermissionsOptions<TKind, TAction>): T;
export function getPermissions<TKind extends string = string, TAction extends string = string>(
  options: GetPermissionsOptions<TKind, TAction>,
): Record<string, boolean>;
export function getPermissions<
  T extends Record<string, boolean> = Record<string, boolean>,
  TKind extends string = string,
  TAction extends string = string,
>({
  permissionConfigs,
  data,
  id,
  scope,
}: GetPermissionsOptions<TKind, TAction>): T | Record<string, boolean> {
  return permissionConfigs.reduce<Record<string, boolean>>((acc, { kind, actionMap }) => {
    const allowedActions = data?.allowedActions({ kind, id, scope }) ?? [];

    const actionPermissions = Object.keys(actionMap).reduce<Record<string, boolean>>(
      (actionAcc, key) => {
        const action = actionMap[key];
        if (Array.isArray(action)) {
          // when action is an array, permission is true only if all actions are allowed
          actionAcc[key] = action.every((a) => allowedActions.includes(a));
        } else if (action !== undefined) {
          actionAcc[key] = allowedActions.includes(action);
        }
        return actionAcc;
      },
      {},
    );

    return {
      ...acc,
      ...actionPermissions,
    };
  }, {});
}
